import * as THREE from "three";
import { createLegoBrick, BRICK_H, STUD } from "../bricks/LegoBrick";
import { BRICK_COLORS, randomPlayColor } from "../bricks/palette";
import { makeFloorPlushMaterial, makePlushMaterial, plush } from "../bricks/plush";
import { quality } from "../quality";

/**
 * Neon cathedral / pedestal arena matching the poster vibe.
 */
export class Arena {
  readonly group = new THREE.Group();
  readonly floorY = 0;
  readonly pedestalTop = 1.4;
  private floaters: THREE.Object3D[] = [];
  private showcase: THREE.Group | null = null;
  private floaterRoot = new THREE.Group();

  constructor() {
    this.group.name = "arena";
    this.buildFloor();
    this.buildPedestal();
    this.buildPillars();
    this.buildBanners();
    this.floaterRoot.name = "floaters";
    this.group.add(this.floaterRoot);
    this.buildFloatingDebris();
    this.buildCenterStack();
  }

  setShowcaseVisible(on: boolean) {
    if (this.showcase) this.showcase.visible = on;
  }

  setFloatersVisible(on: boolean) {
    this.floaterRoot.visible = on;
  }

  update(t: number) {
    for (let i = 0; i < this.floaters.length; i++) {
      const o = this.floaters[i]!;
      const phase = o.userData.phase as number;
      const baseY = o.userData.baseY as number;
      o.position.y = baseY + Math.sin(t * 0.8 + phase) * 0.35;
      o.rotation.x = t * 0.15 + phase;
      o.rotation.y = t * 0.25 + phase * 0.5;
    }
  }

  private buildFloor() {
    const geo = new THREE.CircleGeometry(28, 64);
    const mat = plush.ready
      ? makeFloorPlushMaterial()
      : new THREE.MeshStandardMaterial({
          color: 0x0a0a18,
          roughness: 0.9,
          metalness: 0.2,
        });
    const floor = new THREE.Mesh(geo, mat);
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    this.group.add(floor);

    const ring = new THREE.Mesh(
      new THREE.RingGeometry(10, 10.08, 64),
      new THREE.MeshBasicMaterial({ color: BRICK_COLORS.cyan, transparent: true, opacity: 0.35 }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.02;
    this.group.add(ring);

    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      const line = new THREE.Mesh(
        new THREE.BoxGeometry(0.04, 0.02, 18),
        new THREE.MeshBasicMaterial({
          color: i % 2 === 0 ? BRICK_COLORS.cyan : BRICK_COLORS.brightRed,
          transparent: true,
          opacity: 0.12,
        }),
      );
      line.position.set(Math.sin(a) * 9, 0.03, Math.cos(a) * 9);
      line.rotation.y = a;
      this.group.add(line);
    }
  }

  private buildPedestal() {
    const base = new THREE.Mesh(
      new THREE.CylinderGeometry(3.2, 3.6, 0.35, 48),
      plush.ready
        ? makePlushMaterial(BRICK_COLORS.darkBlue)
        : new THREE.MeshStandardMaterial({
            color: 0x0c1520,
            emissive: BRICK_COLORS.cyan,
            emissiveIntensity: 0.15,
            roughness: 0.4,
            metalness: 0.6,
          }),
    );
    base.position.y = 0.18;
    base.receiveShadow = true;
    this.group.add(base);

    const mid = new THREE.Mesh(
      new THREE.CylinderGeometry(2.6, 3.0, 0.5, 48),
      new THREE.MeshStandardMaterial({
        color: 0x081018,
        emissive: 0x004455,
        emissiveIntensity: 0.4,
        roughness: 0.35,
        metalness: 0.7,
      }),
    );
    mid.position.y = 0.55;
    this.group.add(mid);

    const top = new THREE.Mesh(
      new THREE.CylinderGeometry(2.2, 2.4, 0.25, 48),
      new THREE.MeshStandardMaterial({
        color: 0x102028,
        emissive: BRICK_COLORS.cyan,
        emissiveIntensity: 0.35,
        roughness: 0.3,
        metalness: 0.8,
      }),
    );
    top.position.y = this.pedestalTop - 0.12;
    top.receiveShadow = true;
    this.group.add(top);

    const glow = new THREE.Mesh(
      new THREE.CircleGeometry(2.0, 48),
      new THREE.MeshBasicMaterial({
        color: BRICK_COLORS.cyan,
        transparent: true,
        opacity: 0.15,
      }),
    );
    glow.rotation.x = -Math.PI / 2;
    glow.position.y = this.pedestalTop + 0.02;
    this.group.add(glow);
  }

  private buildPillars() {
    const positions: [number, number][] = [
      [-12, -8],
      [12, -8],
      [-14, 6],
      [14, 6],
      [-8, 12],
      [8, 12],
    ];
    for (const [x, z] of positions) {
      const pillar = new THREE.Mesh(
        new THREE.BoxGeometry(1.2, 14, 1.2),
        new THREE.MeshStandardMaterial({
          color: 0x0a1018,
          roughness: 0.85,
          metalness: 0.15,
        }),
      );
      pillar.position.set(x, 7, z);
      pillar.castShadow = true;
      this.group.add(pillar);

      const strip = new THREE.Mesh(
        new THREE.BoxGeometry(0.08, 12, 0.08),
        new THREE.MeshBasicMaterial({ color: BRICK_COLORS.cyan }),
      );
      strip.position.set(x + 0.55, 6, z + 0.55);
      this.group.add(strip);
    }
  }

  private buildBanners() {
    const mk = (x: number, color: number) => {
      const banner = new THREE.Mesh(
        new THREE.PlaneGeometry(2.2, 5.5),
        new THREE.MeshStandardMaterial({
          color,
          emissive: color,
          emissiveIntensity: 0.25,
          side: THREE.DoubleSide,
          roughness: 0.7,
        }),
      );
      banner.position.set(x, 5.5, -10);
      this.group.add(banner);

      const icon = createLegoBrick({ w: 1, d: 1, h: 0.6 }, 0xffffff, { emissive: true });
      icon.scale.setScalar(0.55);
      icon.position.set(x, 5.8, -9.85);
      this.group.add(icon);
    };
    mk(-7, BRICK_COLORS.brightRed);
    mk(7, BRICK_COLORS.cyan);
  }

  private buildFloatingDebris() {
    const sizes = [
      { w: 1, d: 1 },
      { w: 2, d: 1 },
      { w: 2, d: 2 },
      { w: 1, d: 2 },
      { w: 3, d: 1 },
    ];
    const n = quality.low ? 8 : 28;
    for (let i = 0; i < n; i++) {
      const size = sizes[i % sizes.length]!;
      const b = createLegoBrick(size, randomPlayColor(), { emissive: Math.random() > 0.6 });
      const angle = Math.random() * Math.PI * 2;
      const r = 5 + Math.random() * 14;
      const baseY = 2 + Math.random() * 8;
      b.position.set(Math.cos(angle) * r, baseY, Math.sin(angle) * r);
      b.rotation.set(Math.random() * 2, Math.random() * 2, Math.random() * 2);
      b.scale.setScalar(0.55 + Math.random() * 0.55);
      b.userData.phase = Math.random() * Math.PI * 2;
      b.userData.baseY = baseY;
      this.floaters.push(b);
      this.floaterRoot.add(b);
    }
  }

  /** Showcase stack on the pedestal (poster silhouette) */
  private buildCenterStack() {
    const stack = new THREE.Group();
    stack.name = "showcase-stack";
    this.showcase = stack;
    stack.position.y = this.pedestalTop;
    const layers: { w: number; d: number; color: number }[] = [
      { w: 4, d: 3, color: BRICK_COLORS.blue },
      { w: 3, d: 3, color: BRICK_COLORS.red },
      { w: 2, d: 2, color: BRICK_COLORS.yellow },
      { w: 2, d: 1, color: BRICK_COLORS.cyan },
      { w: 1, d: 1, color: BRICK_COLORS.orange },
    ];
    let y = 0;
    for (const layer of layers) {
      const b = createLegoBrick(layer, layer.color, {
        emissive: layer.color === BRICK_COLORS.cyan,
      });
      b.position.y = y + BRICK_H / 2;
      y += BRICK_H;
      stack.add(b);
    }
    this.group.add(stack);
  }

  /** Call when play starts so player bricks don't clip the poster stack. */
  clearPedestalForPlay() {
    this.setShowcaseVisible(false);
  }

  restorePedestal() {
    this.setShowcaseVisible(true);
  }

  snapToGrid(x: number, z: number, step = STUD): { x: number; z: number } {
    return {
      x: Math.round(x / step) * step,
      z: Math.round(z / step) * step,
    };
  }
}
